import {arrayMove} from '@dnd-kit/sortable';
import type {DragEndEvent} from '@dnd-kit/core';
import type {FabricObject} from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {GROUP_HEADER_ROW_PREFIX} from './layers-panel.types';
import type {DraggedLayer, LayerEntry, LayerGroupEntry} from './layers-panel.types';

export function getSortableId(entry: LayerEntry): string {
  if (entry.layerType === 'item') {
    return entry.uid;
  }
  return `${GROUP_HEADER_ROW_PREFIX}${entry.groupId}`;
}

export function getDraggedLayer(activeId: string, entries: LayerEntry[]): DraggedLayer | null {
  if (activeId.startsWith(GROUP_HEADER_ROW_PREFIX)) {
    const groupId = activeId.slice(GROUP_HEADER_ROW_PREFIX.length);
    const entry = entries.find((e): e is LayerGroupEntry => e.layerType === 'group' && e.groupId === groupId);
    return entry ? {layerType: 'group', entry} : null;
  }
  const exists = entries.some((e) => (e.layerType === 'item' ? e.uid === activeId : e.memberUids.includes(activeId)));
  return exists ? {layerType: 'item', uid: activeId} : null;
}

export function areLayerEntriesEqual(a: LayerEntry[], b: LayerEntry[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    const entryA = a[i];
    const entryB = b[i];
    if (entryA.layerType !== entryB.layerType) {
      return false;
    }
    if (entryA.layerType === 'item' && entryB.layerType === 'item') {
      if (entryA.uid !== entryB.uid) {
        return false;
      }
    } else if (entryA.layerType === 'group' && entryB.layerType === 'group') {
      if (entryA.groupId !== entryB.groupId || entryA.memberUids.length !== entryB.memberUids.length) {
        return false;
      }
      for (let j = 0; j < entryA.memberUids.length; j += 1) {
        if (entryA.memberUids[j] !== entryB.memberUids[j]) {
          return false;
        }
      }
    }
  }
  return true;
}

export function applySelection(page: Page, uids: readonly string[], selectionEvent?: MouseEvent): void {
  if (uids.length === 0) {
    page.activeSelection.discardActiveObject();
    return;
  }
  const fabricObjects = uids.map((uid) => page.items.getItem(uid)?.fabricObject).filter((f): f is FabricObject => f !== undefined);
  if (fabricObjects.length === 0) {
    return;
  }
  if (fabricObjects.length === 1) {
    page.activeSelection.setActiveObject(fabricObjects[0], selectionEvent);
    return;
  }
  page.activeSelection.selectFabricObjects(fabricObjects);
}

function countPmwInEntries(entries: LayerEntry[], stopIdx: number): number {
  let count = 0;
  for (let i = 0; i < stopIdx && i < entries.length; i += 1) {
    const e = entries[i];
    if (e.layerType === 'item') {
      count += 1;
    } else {
      count += e.memberUids.length;
    }
  }
  return count;
}

export function handleDragEnd(event: DragEndEvent, entries: LayerEntry[]): void {
  const {active, over} = event;
  if (!over || active.id === over.id) {
    return;
  }
  const activeId = String(active.id);
  const overId = String(over.id);

  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!page) {
    return;
  }

  const outerIds = new Set(entries.map(getSortableId));
  if (outerIds.has(activeId) && outerIds.has(overId)) {
    reorderMainListRow(activeId, overId, entries, page);
    return;
  }
  const group = entries.find((e): e is LayerGroupEntry => e.layerType === 'group' && e.memberUids.includes(activeId));
  if (group?.memberUids.includes(overId)) {
    reorderGroupMember(activeId, overId, group, page);
  }
}

function reorderMainListRow(activeId: string, overId: string, entries: LayerEntry[], page: Page): void {
  const sourceIdx = entries.findIndex((e) => getSortableId(e) === activeId);
  const destIdx = entries.findIndex((e) => getSortableId(e) === overId);
  if (sourceIdx < 0 || destIdx < 0) {
    return;
  }
  const newEntries = arrayMove(entries, sourceIdx, destIdx);
  const pmwAbove = countPmwInEntries(newEntries, destIdx);
  const totalPmwItems = countPmwInEntries(entries, entries.length);
  const targetTopPmwZ = totalPmwItems - 1 - pmwAbove;

  const sourceEntry = entries[sourceIdx];
  if (sourceEntry.layerType === 'group') {
    page.items.moveGroupToPmwZIndex(sourceEntry.groupId, targetTopPmwZ);
    return;
  }
  const item = page.items.getItem(sourceEntry.uid);
  if (item) {
    page.items.moveItemToPmwZIndex(item, targetTopPmwZ);
  }
}

function reorderGroupMember(activeId: string, overId: string, entry: LayerGroupEntry, page: Page): void {
  const destIdx = entry.memberUids.indexOf(overId);
  const topPmwZ = page.items.getItemIdsInOrder().indexOf(entry.memberUids[0]);
  if (destIdx < 0 || topPmwZ < 0) {
    return;
  }
  const item = page.items.getItem(activeId);
  if (!item) {
    return;
  }
  page.items.moveGroupedItemToPmwZIndex(item, topPmwZ - destIdx);
}
